import { SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { CommonCalendarErrorType, CommonCalendarEvent, CommonCalendarQueryParams } from './entities';
import { CommonCalendarRestful } from './restful';
import { CommonCalendarEnvironmentProps } from './environment';
import { CommonCalendarRequestOptions } from './internalEntities';

export class CommonCalendarRequest<
  T extends string,
  E,
  P extends CommonCalendarQueryParams = CommonCalendarQueryParams,
> extends Subscribable<CommonCalendarEvent<T, E>> {
  private restful: CommonCalendarRestful;
  private calendarTypeName: string;
  private options: CommonCalendarRequestOptions<E, P>;

  constructor(
    session: Session,
    env: CommonCalendarEnvironmentProps,
    calendarTypeName: string,
    options: CommonCalendarRequestOptions<E, P> = {},
  ) {
    super();
    this.options = options;
    this.restful = new CommonCalendarRestful(session, env);
    this.calendarTypeName = calendarTypeName;
  }

  public fetchCalendarData = async (params: P): SafePromise<E[]> => {
    const calendarDataResponse = await this.restful.fetchCalendarData<unknown, P>(params, {
      credentials: this.options.credentials,
      genUrlParams: this.options.genUrlParams,
    });

    if (calendarDataResponse.err) {
      this.dispatch({
        error: calendarDataResponse.err,
        errorType: CommonCalendarErrorType.GET_DATA,
        parameters: params,
        type: `${this.calendarTypeName as T}:error`,
      });
      return { err: calendarDataResponse.err };
    }

    const result = this.options.formatResponse
      ? this.options.formatResponse(calendarDataResponse.ok)
      : (calendarDataResponse.ok as Record<string, E[]>)?.[this.calendarTypeName] || [];

    this.dispatch({
      result,
      type: `${this.calendarTypeName as T}:fetched_data`,
    });

    return { ok: result };
  };
}
